<template>
  <div id="app">
    <StartupProcessComponent v-if="!webViewModeStore.isWebViewMode" />
    <component :is="layout" v-if="pageReady" @vue:mounted="contentsReady"/>
    <AppErrorWatchDialog />
    <MakePhoneCallDialog v-if="pageReady && accountInfo && !route.meta.isPublic && !webViewModeStore.isWebViewMode" />
    <ReceivePhoneCallDialog v-if="pageReady && accountInfo && !route.meta.isPublic && !webViewModeStore.isWebViewMode" />
    <SessionCheckerComponent/>
    <ModalsContainer />
    <OperationLockOverlay v-if="!webViewModeStore.isWebViewMode" />
  </div>
</template>

<script lang="ts">
export default {
  name: 'App',
}
</script>

<script setup lang="ts">
  import { computed, watch, nextTick, onMounted, onBeforeUnmount, onErrorCaptured, defineAsyncComponent } from 'vue'
  import { useRoute } from 'vue-router'
  import { errorDefaultMessage } from '@/utils/errors'
  import * as loggers from "@/utils/loggers"
  import { ModalsContainer } from 'vue-final-modal'
  import { useVersionChecksStore } from "@/stores/versionChecks"
  import { useSessionIdStore } from "@/stores/sessionId"
  import { useLoginStore } from '@/stores/login'
  import { useAppErrorStore } from "@/stores/appError"
  import { useWebViewModeStore } from '@/stores/webViewMode'
  import AppErrorWatchDialog from "@/components/cloud/error/AppErrorWatchDialog.vue"
  import OperationLockOverlay from "@/components/cloud/operationLock/OperationLockOverlay.vue"
  const loginStore = useLoginStore()
  const versionChecksStore = useVersionChecksStore()
  const sessionIdStore = useSessionIdStore()
  const appErrorStore = useAppErrorStore()
  const webViewModeStore = useWebViewModeStore()
  const route = useRoute()

  // components
  const SessionCheckerComponent = defineAsyncComponent(() => import("@/components/cloud/session/SessionCheckerComponent.vue"))
  const StartupProcessComponent = defineAsyncComponent(() => import("@/components/cloud/notification/StartupProcessComponent.vue"))
  const MakePhoneCallDialog = defineAsyncComponent(() => import("@/components/cloud/denwa/MakePhoneCallDialog.vue"))
  const ReceivePhoneCallDialog = defineAsyncComponent(() => import("@/components/cloud/denwa/ReceivePhoneCallDialog.vue"))

  // computed
  const layout = computed(() => {
    if (webViewModeStore.isWebViewMode) return 'webview-layout'
    return (route.meta.layout || 'default') + '-layout';
  })
  const accountInfo = computed(() => {
    return loginStore.currentAccount
  })
  const pageReady = computed(() => {
    if(!route.name) return false;
    if(route.meta.isPublic) return true;
    if(!accountInfo.value) return false;
    if(!accountInfo.value.account_id) return false;
    return true;
  })
  // watch
  watch(
    () => pageReady.value,
    (pageReady) => {
      if(pageReady){
        const sessionId = sessionIdStore.getSessionId();
        if(sessionId) {
          loggers.setSessionId(sessionId.substring(0, 8))
        }
        if(accountInfo.value){
          loggers.setAccountId(accountInfo.value.account_id)
        }
        loggers.setKuraemonCloudWebPageVersion(versionChecksStore.storedKuraemonCloudWebPageVersion)

        loggers.info(`page ready`)
      }
    },
  )
  // methods
  const contentsReady = () => {
    nextTick(() => { 
      const initialLoading = document.querySelector(".initial-loading-wrap") as HTMLElement
      if(initialLoading){
        initialLoading.style.display = 'none'
      }
    })
  }
  const handleUnhandledrejection = (e: PromiseRejectionEvent) => {
    console.warn('unhandledrejection', e)
    loggers.warn("unhandledrejection", {
      kCloudAppError: {
        error: e,
        reason: (e.reason ? e.reason.toString() : e.toString()),
      }
    })
  }
  onMounted(() => {
    window.addEventListener('unhandledrejection', handleUnhandledrejection)
  })
  onBeforeUnmount(() => {
    window.removeEventListener('unhandledrejection', handleUnhandledrejection)
  })
  onErrorCaptured((err, vm, info) => {
    console.warn(err, vm, info)
    appErrorStore.updateAppError({
      error: err,
      customErrorCode: 'PE00',
      description: errorDefaultMessage
    })
    return true;
  })
   // created
  const created = async() => {
    // リリースバージョンのチェック
    await versionChecksStore.checkWebPageVersion();
  }
  created()
</script>

<style>
/* Web は Meiryo、ネイティブ WebView 埋め込み時のみネイティブと同じ丸ゴ。切替は stores/webViewMode.ts */
@font-face {
  font-family: "LEC-GMaruGo";
  src: url('@/assets/fonts/LEC-GMaruGo-DB.woff2') format("woff2");
  /* 単一ウェイト(DB)を全域に割り当て、bold でも faux-bold を出さずネイティブと揃える */
  font-weight: 100 900;
  font-style: normal;
  font-display: swap;
}

:root {
  --app-font: Meiryo, sans-serif;
}

:root.is-webview {
  --app-font: "LEC-GMaruGo", Meiryo, sans-serif;
}

html {
  box-sizing: border-box;
  font-size: 14px;
  font-family: var(--app-font);
  height: 100%;
}
body {
  margin: 0;
  font-size: 1rem;
  height: 100%;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
}
#app {
  height: 100%;
}
button,
input,
select,
textarea {
  font-family: inherit;
}
button {
  outline: none;
}
*,
*::before,
*::after {
  box-sizing: inherit;
}
</style>
